/**
 * Utilities for parsing and storing date and time separately.
 * Use when persisting to sales_transactions so each date type has date + time columns when present.
 */

export interface ParsedDateAndTime {
  date: string | null;
  time: string | null;
}

/**
 * Extract YYYY-MM-DD from a Date using LOCAL time components (avoids UTC shift).
 */
export function dateToLocalDateString(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Extract HH:mm:ss from a Date using LOCAL time components.
 */
function dateToLocalTimeString(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

/**
 * Convert an Excel date serial number to YYYY-MM-DD + optional HH:mm:ss.
 * Uses UTC epoch to avoid timezone shifts.
 */
function excelSerialToDateParts(serial: number): { date: string; time: string | null } {
  const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);
  const ms = EXCEL_EPOCH_UTC + serial * 86400000;
  const d = new Date(ms);
  const dateStr = d.toISOString().split('T')[0];
  const hasFraction = serial % 1 !== 0;
  const timeStr = hasFraction ? d.toISOString().split('T')[1]?.split('.')[0] ?? null : null;
  return { date: dateStr, time: timeStr };
}

/**
 * Try to parse a date string in flexible formats: YYYY-MM-DD, MM/DD/YYYY, M/D/YYYY, MM-DD-YYYY.
 * Returns YYYY-MM-DD or null. Does NOT use new Date() for ambiguous formats.
 */
function parseFlexibleDateString(s: string): string | null {
  const trimmed = s.trim();

  // YYYY-MM-DD
  const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    return `${isoMatch[1]}-${String(isoMatch[2]).padStart(2, '0')}-${String(isoMatch[3]).padStart(2, '0')}`;
  }

  // MM/DD/YYYY or M/D/YYYY (slash or dash separated)
  const usMatch = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (usMatch) {
    const month = parseInt(usMatch[1], 10);
    const day = parseInt(usMatch[2], 10);
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${usMatch[3]}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  return null;
}

/**
 * Parse a value that may be date-only or datetime into separate date (YYYY-MM-DD) and time (HH:mm:ss).
 * Handles: ISO strings, "YYYY-MM-DD HH:mm", "MM/DD/YYYY HH:mm", Excel date serial, Date objects.
 */
export function parseDateAndTime(value: unknown): ParsedDateAndTime {
  if (value === null || value === undefined || value === '') {
    return { date: null, time: null };
  }

  // Date object
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return { date: null, time: null };
    return { date: dateToLocalDateString(value), time: dateToLocalTimeString(value) };
  }

  // Excel date serial
  if (typeof value === 'number') {
    return excelSerialToDateParts(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return { date: null, time: null };

    // ISO with T (e.g. 2024-01-15T14:30:00Z or 2024-01-15T14:30:00)
    if (trimmed.includes('T')) {
      const d = new Date(trimmed);
      if (isNaN(d.getTime())) return { date: null, time: null };
      // ISO strings with Z are UTC; without Z are local. Use the Date object properly.
      if (trimmed.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(trimmed)) {
        const dateStr = d.toISOString().split('T')[0];
        const timeStr = d.toISOString().split('T')[1]?.split('.')[0] ?? null;
        return { date: dateStr, time: timeStr };
      }
      return { date: dateToLocalDateString(d), time: dateToLocalTimeString(d) };
    }

    // Split on whitespace to separate date part from optional time part
    const parts = trimmed.split(/\s+/);
    const datePart = parts[0];
    const timePart = parts.length > 1 ? parts.slice(1).join(' ') : null;

    // Try to parse the date part
    const parsedDate = parseFlexibleDateString(datePart);

    if (parsedDate) {
      const parsedTime = timePart ? normalizeTimeString(timePart) : null;
      return { date: parsedDate, time: parsedTime };
    }

    // Last resort: try new Date() but extract in local time
    const d = new Date(trimmed);
    if (!isNaN(d.getTime())) {
      const hadTime = /\d{1,2}:\d{2}/.test(trimmed);
      return {
        date: dateToLocalDateString(d),
        time: hadTime ? dateToLocalTimeString(d) : null,
      };
    }
  }

  return { date: null, time: null };
}

/**
 * Normalize a time string to HH:mm:ss (24-hour, no timezone).
 * Handles: "14:30", "14:30:00", "2:30 PM", "2:30:00 AM", Excel time fractions.
 */
export function normalizeTimeString(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;

  // Excel time fraction (0.0 - 0.9999...)
  if (typeof value === 'number') {
    const fraction = value % 1;
    if (fraction === 0 && value > 1) return null;
    const totalSeconds = Math.round(Math.abs(fraction) * 24 * 60 * 60);
    const hours = Math.floor(totalSeconds / 3600) % 24;
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  const s = String(value).trim();

  // Handle AM/PM format: "2:30 PM", "11:30:00 AM"
  const ampmMatch = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (ampmMatch) {
    let hours = parseInt(ampmMatch[1], 10);
    const minutes = parseInt(ampmMatch[2], 10);
    const seconds = ampmMatch[3] !== undefined ? parseInt(ampmMatch[3], 10) : 0;
    const isPM = ampmMatch[4].toUpperCase() === 'PM';
    if (hours === 12) hours = isPM ? 12 : 0;
    else if (isPM) hours += 12;
    return `${String(Math.min(23, hours)).padStart(2, '0')}:${String(Math.min(59, minutes)).padStart(2, '0')}:${String(Math.min(59, seconds)).padStart(2, '0')}`;
  }

  // 24-hour format: "14:30" or "14:30:00"
  const match = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const [, h, m, sec] = match;
  const hours = Math.min(23, Math.max(0, parseInt(h!, 10)));
  const minutes = Math.min(59, Math.max(0, parseInt(m!, 10)));
  const seconds = sec !== undefined ? Math.min(59, Math.max(0, parseInt(sec, 10))) : 0;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Detect if a value looks like combined date and time.
 */
export function isCombinedDateTime(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  if (typeof value === 'number') return value % 1 !== 0;
  const s = String(value).trim();
  if (s.includes('T') && /\d{1,2}:\d{2}/.test(s)) return true;
  if (/^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}/.test(s)) return true;
  if (/\d{1,2}:\d{2}(:\d{2})?/.test(s) && /\d{1,4}[\/\-]\d{1,2}[\/\-]\d{1,4}/.test(s)) return true;
  return false;
}

/**
 * Parse a value that may be combined date+time, date-only, or time-only.
 */
export function parseDateTimeForSales(value: unknown): ParsedDateAndTime {
  const parsed = parseDateAndTime(value);
  if (parsed.date != null || parsed.time != null) return parsed;
  const timeOnly = normalizeTimeString(value);
  if (timeOnly != null) return { date: null, time: timeOnly };
  return { date: null, time: null };
}
